/** Build / parse the swaps catalog used by /swaps and compare. */

const SWAP_FIELDS = [
  "slug",
  "name",
  "short",
  "tripleSwapDay",
  "swapFree",
  "swapFreeNote",
  "swapUpdated",
  "swapSource",
  "swaps",
];

export function buildSwapsCatalog(brokers) {
  return {
    type: "swaps-catalog",
    version: 1,
    exportedAt: new Date().toISOString(),
    note: "Edit swaps / swapFree / tripleSwapDay / timestamps, then upload in Admin → Swaps & compare. Slugs must already exist on the site.",
    brokers: brokers.map((b) => ({
      slug: b.slug,
      name: b.name,
      short: b.short,
      tripleSwapDay: b.tripleSwapDay,
      swapFree: b.swapFree,
      swapFreeNote: b.swapFreeNote ?? "",
      swapUpdated: b.swapUpdated ?? null,
      swapSource: b.swapSource ?? "Daily snapshot",
      swaps: b.swaps || {},
    })),
  };
}

export function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function validateSwapEntry(entry, index) {
  if (!entry || typeof entry !== "object") {
    return `brokers[${index}] must be an object`;
  }
  if (typeof entry.slug !== "string" || !entry.slug.trim()) {
    return `brokers[${index}].slug is required`;
  }
  if (entry.swaps !== undefined) {
    if (!entry.swaps || typeof entry.swaps !== "object" || Array.isArray(entry.swaps)) {
      return `brokers[${index}].swaps must be an object`;
    }
    for (const [inst, legs] of Object.entries(entry.swaps)) {
      if (!legs || typeof legs !== "object") {
        return `brokers[${index}].swaps.${inst} must be { long, short }`;
      }
      if (typeof legs.long !== "number" || typeof legs.short !== "number") {
        return `brokers[${index}].swaps.${inst} long/short must be numbers`;
      }
    }
  }
  if (entry.tripleSwapDay !== undefined && typeof entry.tripleSwapDay !== "number") {
    return `brokers[${index}].tripleSwapDay must be a number`;
  }
  if (entry.swapFree !== undefined && typeof entry.swapFree !== "boolean") {
    return `brokers[${index}].swapFree must be a boolean`;
  }
  return null;
}

/** Accepts swaps-catalog object, or a bare array of swap patches / full brokers. */
export function parseSwapsUpload(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: "Invalid JSON — check commas, quotes, and brackets." };
  }

  let entries;
  if (Array.isArray(parsed)) {
    entries = parsed;
  } else if (parsed && typeof parsed === "object" && Array.isArray(parsed.brokers)) {
    entries = parsed.brokers;
  } else {
    return {
      ok: false,
      error: "Expected a swaps-catalog object ({ type, brokers: [...] }) or an array of broker swap entries.",
    };
  }

  if (!entries.length) {
    return { ok: false, error: "No brokers found in the upload." };
  }

  for (let i = 0; i < entries.length; i += 1) {
    const err = validateSwapEntry(entries[i], i);
    if (err) return { ok: false, error: err };
  }

  return { ok: true, entries };
}

/**
 * Merge swap patches onto the live catalog.
 * Returns full broker objects ready to upsert (seed brokers become custom overrides).
 */
export function applySwapPatches(liveBrokers, patches) {
  const bySlug = new Map(liveBrokers.map((b) => [b.slug, b]));
  const updated = [];
  const missing = [];

  for (const patch of patches) {
    const slug = patch.slug.trim().toLowerCase().replace(/\s+/g, "-");
    const current = bySlug.get(slug) || bySlug.get(patch.slug);
    if (!current) {
      missing.push(slug);
      continue;
    }

    const next = {
      ...current,
      tripleSwapDay: patch.tripleSwapDay ?? current.tripleSwapDay,
      swapFree: patch.swapFree ?? current.swapFree,
      swapFreeNote: patch.swapFreeNote ?? current.swapFreeNote,
      swapUpdated: patch.swapUpdated !== undefined ? patch.swapUpdated : current.swapUpdated,
      swapSource: patch.swapSource ?? current.swapSource,
      swaps: patch.swaps
        ? { ...current.swaps, ...patch.swaps }
        : current.swaps,
      _source: "admin",
    };

    // Drop React/internal-only noise if any
    const clean = { ...next };
    updated.push(clean);
  }

  return { updated, missing, fields: SWAP_FIELDS };
}
